import { useQuery } from 'react-query'
import { Link, useSearchParams } from 'react-router-dom'

import { PageHeader } from '@/components/layout/page/page-header.tsx'
import { Page } from '@/components/layout/page/page.tsx'
import { Button } from '@/components/ui/button.tsx'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs.tsx'
import { Tag } from '@/components/ui/tag.tsx'
import { useCampaigns } from '@/modules/campaigns/state/hooks'
import { LicensesPageContentWithCampaign } from '@/modules/campaigns/views/campaign-licenses-page'
import { EndTrialSection } from '@/modules/licenses/components/end-trial'
import { LicenseOverview } from '@/modules/licenses/components/licenses-overview.tsx'
import { LicensesPageSkeleton } from '@/modules/licenses/components/licenses-page-skeleton.tsx'
import { ModifyLicenseContext } from '@/modules/licenses/components/modify-license-context.tsx'
import { billingService } from '@/modules/licenses/service/billing-service.ts'
import { LicenseMode } from '@/modules/licenses/types'
import { AppRoutes } from '@/utils/constants'
import { handleError } from '@/utils/error'

export const LicensesPage = () => {
  const { activeCampaign, currentDiscount } = useCampaigns()
  const {
    data: plans,
    isFetching,
    refetch,
  } = useQuery(['billing'], billingService.getPlans, {
    onError: (error) => handleError(error, 'Failed to get plans', refetch),
  })
  const [params] = useSearchParams()
  const defaultTab = params.get('mode') ?? LicenseMode.MONTHLY

  if (isFetching || !plans) {
    return <LicensesPageSkeleton />
  }

  const pageHeader = (
    <>
      <PageHeader
        title="Licenses"
        description="Get a License to connect a creator with CreatorHero"
        className="mb-4 sm:flex-row sm:justify-between"
        optionComponents={
          <Link
            to={AppRoutes.Licenses.YourLicenses.asUrl()}
            className="truncate"
          >
            <Button>Your Licenses</Button>
          </Link>
        }
      />
      <EndTrialSection />
    </>
  )

  const tabsList = (
    <TabsList className="grid w-[400px] grid-cols-2">
      <TabsTrigger value={LicenseMode.MONTHLY}>Monthly</TabsTrigger>
      <TabsTrigger
        value={LicenseMode.YEARLY}
        className="gap-2"
      >
        Yearly
        <Tag className="h-fit bg-primary-400 text-white dark:bg-primary-500">
          -25%
        </Tag>
      </TabsTrigger>
    </TabsList>
  )

  const tabsContent = (
    <>
      <TabsContent value={LicenseMode.MONTHLY}>
        <LicenseOverview
          data={plans}
          mode={LicenseMode.MONTHLY}
          showDiscount={!!activeCampaign && currentDiscount > 0}
        />
      </TabsContent>
      <TabsContent value={LicenseMode.YEARLY}>
        <LicenseOverview
          data={plans}
          mode={LicenseMode.YEARLY}
        />
      </TabsContent>
    </>
  )

  const pageContent = (
    <>
      {pageHeader}
      <Tabs defaultValue={defaultTab}>
        {tabsList}
        {tabsContent}
      </Tabs>
    </>
  )

  return (
    <ModifyLicenseContext.Provider value={{ licenses: null, plans }}>
      <Page>
        <LicensesPageContentWithCampaign
          header={pageHeader}
          tabsContent={tabsContent}
          tabsList={tabsList}
          defaultPageContent={pageContent}
        />
      </Page>
    </ModifyLicenseContext.Provider>
  )
}
